// 14 · Vincular Telegram — código real de la callable
// requestTelegramBindingCode (formato EE-XXXX-XXXX, TTL 10 min), deep link
// https://t.me/EdgeEliteBot?start=<código>, confirmación por onSnapshot del
// doc /telegramBindings/{code} (used:true). Estado vinculado con toggles
// por tipo de mensaje (users/{uid}.alertPrefs) y última señal entregada.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { doc, onSnapshot } from 'firebase/firestore';
import { firestore, functions } from '../../../firebase';
import { useAuth } from '../../../auth/AuthContext';
import useClientStats from '../../hooks/useClientStats';
import { matchOf, pickOf, fmtEdge } from '../../components/SignalCard';
import { Nav, I, useToast } from '../ui';
import { relTime, ventanaDe } from '../hooks';

const BOT = 'EdgeEliteBot';
const TTL_S = 10 * 60;

export default function Telegram() {
  const navigate = useNavigate();
  const toast = useToast();
  const { userData, hasActiveAccess, isAdmin } = useAuth();
  const { signals } = useClientStats();
  const vinculado = !!userData?.telegramChatId;
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [linked, setLinked] = useState(false);
  const [left, setLeft] = useState(TTL_S);
  const [nonce, setNonce] = useState(0);

  // Pedir código (solo si no está vinculado y tiene acceso: la CF rechaza si no)
  useEffect(() => {
    if (vinculado || !(hasActiveAccess || isAdmin)) return undefined;
    let cancel = false;
    setCode(''); setError(''); setLeft(TTL_S);
    (async () => {
      try {
        const r = await httpsCallable(functions, 'requestTelegramBindingCode')();
        if (cancel) return;
        const c = r.data && r.data.code;
        if (!c) throw new Error('sin código');
        setCode(c);
      } catch (e) {
        if (!cancel) setError('No pudimos generar tu código. Vuelve a intentarlo.');
      }
    })();
    return () => { cancel = true; };
  }, [vinculado, hasActiveAccess, isAdmin, nonce]);

  // Cuenta regresiva del código (TTL 10 min de la CF)
  useEffect(() => {
    if (!code || left <= 0) return undefined;
    const t = setTimeout(() => setLeft((v) => v - 1), 1000);
    return () => clearTimeout(t);
  }, [code, left]);

  // Confirmación: el bot marca used:true en /telegramBindings/{code}
  useEffect(() => {
    if (!code) return undefined;
    const unsub = onSnapshot(doc(firestore, 'telegramBindings', code), (s) => {
      if (s.data() && s.data().used === true) setLinked(true);
    }, () => {});
    return () => unsub();
  }, [code]);

  const deep = code ? `https://t.me/${BOT}?start=${encodeURIComponent(code)}` : `https://t.me/${BOT}`;
  const abrir = async () => {
    if (code) { try { await navigator.clipboard.writeText(`/start ${code}`); } catch (_e) { /* noop */ } }
    window.open(deep, '_blank', 'noopener,noreferrer');
  };
  const copiar = async () => {
    try { await navigator.clipboard.writeText(code); toast('Código copiado', { ok: true, ms: 2000 }); } catch (_e) { toast('No pudimos copiar el código'); }
  };
  const mm = `${String(Math.floor(left / 60)).padStart(2, '0')}:${String(left % 60).padStart(2, '0')}`;
  const partes = code ? code.split('-') : ['EE', '····', '····'];
  const ultima = signals[0];

  // ── Estado vinculado ──
  if (vinculado || linked) {
    const handle = userData?.telegramUsername ? `@${userData.telegramUsername}` : 'tu Telegram';
    return (
      <div className="v3"><div className="scroll nt safe-top">
        <Nav title="Canal de Telegram" onBack={() => navigate('/app/perfil')} />
        <div className="tgm-hero">
          <div className="ic ok"><I.check width={28} height={28} stroke="#4ADE80" strokeWidth="2.6" /></div>
          <h2>Vinculado a {handle}</h2>
          <p>Recibes cada señal en Telegram{userData?.telegramLinkedAt?.seconds ? ` desde el ${new Intl.DateTimeFormat('es-CL', { day: 'numeric', month: 'long', timeZone: 'America/Santiago' }).format(new Date(userData.telegramLinkedAt.seconds * 1000))}` : ''}.</p>
        </div>
        {/* "Qué se envía" (toggles): OCULTO hasta que el bot lea alertPrefs
            (decisión Matías 2026-09-10). */}
        {ultima && (
          <>
            <p className="grp">Última señal enviada</p>
            <div className="nf"><div className="ic d"><I.send width={16} height={16} /></div><div className="b">
              <p className="k">Entregada<span className="tm">{relTime(ultima.started_at || 0)}</span></p>
              <p className="tt">ENTRAR · {pickOf(ultima)}</p>
              <p className="dd">{matchOf(ultima)} · {ventanaDe(ultima)}{fmtEdge(ultima.edge_initial ?? ultima.edge) ? ` · ${fmtEdge(ultima.edge_initial ?? ultima.edge)}` : ''}</p>
            </div></div>
          </>
        )}
        <p className="v3hint" style={{ textAlign: 'center', marginTop: 18 }}>Para desvincular tu Telegram escríbenos a contacto@edgeelite.cl y lo hacemos por ti.</p>
      </div></div>
    );
  }

  // ── Sin acceso: la CF no emite código ──
  if (!(hasActiveAccess || isAdmin)) {
    return (
      <div className="v3"><div className="scroll nt safe-top">
        <Nav title="Canal de Telegram" onBack={() => navigate('/app/perfil')} />
        <div className="tgm-hero">
          <div className="ic"><I.send width={28} height={28} strokeWidth="2" /></div>
          <h2>Recibe las señales<br />también en Telegram</h2>
          <p>El canal de Telegram forma parte del plan. Con tu acceso activo podrás vincularlo desde aquí.</p>
        </div>
      </div></div>
    );
  }

  // ── Flujo de vinculación ──
  return (
    <div className="v3"><div className="scroll nt safe-top">
      <Nav title="Canal de Telegram" onBack={() => navigate('/app/perfil')} />
      <div className="tgm-hero">
        <div className="ic"><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#FF4D0D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 4L3 11l7 2 2 7z" /><path d="M10 13l11-9" /></svg></div>
        <h2>Recibe las señales<br />también en Telegram</h2>
        <p>Las mismas señales, en el mismo segundo. Útil si silencias las notificaciones del teléfono.</p>
        <div className="code">{partes.map((p, i) => <b key={i} className="w">{p}</b>)}</div>
        <p className="exp">{error ? error : code ? (left > 0 ? `El código vence en ${mm}` : 'El código venció') : 'Generando código…'}</p>
      </div>
      <div className="steps">
        <div className="stp"><span className="n">1</span><p className="b">Abre <b>@{BOT}</b> en Telegram.</p></div>
        <div className="stp"><span className="n">2</span><p className="b">Envía <code>/start {code || 'EE-XXXX-XXXX'}</code> o toca el botón de abajo, que lo hace por ti.</p></div>
        <div className="stp"><span className="n">3</span><p className="b">Vuelve a la app. La vinculación se confirma sola.</p></div>
      </div>
      {left > 0 && !error ? (
        <button type="button" className="cta" style={{ marginTop: 14 }} onClick={abrir} disabled={!code}>Abrir Telegram<I.arrow /></button>
      ) : (
        <button type="button" className="cta" style={{ marginTop: 14 }} onClick={() => setNonce((n) => n + 1)}>Generar un código nuevo<I.arrow /></button>
      )}
      <button type="button" className="cta ghost sm" style={{ marginTop: 9 }} onClick={copiar} disabled={!code}>Copiar código</button>
      <p className="v3hint" style={{ textAlign: 'center' }}>Puedes desvincular en cualquier momento escribiéndonos a contacto@edgeelite.cl.</p>
    </div></div>
  );
}
